import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import useBrowseTypeAndLetter, {
  getFetchItemsPath,
} from 'utils/useBrowseTypeAndLetter';
import Layout from 'components/Layout';
import hasAuthToken from 'lib/hasAuthToken';
import Breadcrumbs from 'components/Breadcrumbs';
import Properties from 'components/Properties';
import Pagination from 'components/Pagination';
import { slugifyVacancy } from 'lib/slugify';
import {
  BROWSE_TYPES,
  BROWSE_TYPE_EMPLOYERS,
  BROWSE_TYPE_PLACES,
  BROWSE_TYPE_VACANCIES,
} from 'pages/bladeren';
import AlphabetLinkList from '@molecules/AlphabetLinkList/AlphabetLinkList';
import Company from 'svg/icon-company.svg';
import Location from 'svg/icon-pin.svg';
import { useRef } from 'react';

function getItemUrl({ type, item }) {
  switch (type) {
    case BROWSE_TYPE_EMPLOYERS:
      return `/vacatures/${item._source.slug}`;
    case BROWSE_TYPE_VACANCIES:
      return `/vacature/${slugifyVacancy(
        item._source.slug,
        item._source.location
      )}/${item._source.id}`;
    case BROWSE_TYPE_PLACES:
      return `/vacatures/plaats/${item.key}`;
    default:
      return '/';
  }
}

function ListItem({ type, item, t }) {
  const title = item.key || item._source.title || item._source.name;

  return (
    <div className="u-margin-bottom-base">
      <h3 className="h4">
        <Link href={getItemUrl({ type, item })} className="u-umbrella-link">
          {title}
        </Link>
      </h3>
      {item._source && (
        <Properties
          extraClasses="c-properties__property--spacing-small"
          properties={[
            {
              Icon: Company,
              label: t('property.company'),
              value:
                item._source.companyBranch?.name || item._source.company?.name,
            },
            {
              Icon: Location,
              label: t('property.location'),
              value: item._source.address.city,
            },
          ]}
        />
      )}
    </div>
  );
}

function BrowseTypeAndLetterPage({
  query,
  href,
  items: initItems,
  totalResults: initTotalResults,
  limit = 20,
}) {
  const { t } = useTranslation();
  const scrollToRef = useRef(null);
  const { type, letter } = query;
  const {
    items,
    totalResults,
    loading,
    currentPage,
    totalPages,
    handleNextPage,
    handlePrevPage,
  } = useBrowseTypeAndLetter({
    type,
    letter,
    initItems,
    initTotalResults,
    scrollToRef,
    limit,
  });
  const title = `Alle ${type} met de letter '${letter.toUpperCase()}'`;

  return (
    <Layout title={title}>
      <div className="o-retain o-retain--wall u-margin-top-huge u-margin-bottom-huge">
        <Breadcrumbs
          links={[
            {
              label: t('browse.pageTitle'),
              href: '/bladeren',
            },
            {
              label: title,
              href,
            },
          ]}
        />
        <h1 className="u-margin-bottom-base">{title}</h1>

        <h2 className="h3 u-text-capitalize">{type}</h2>
        <AlphabetLinkList type={type} />

        <h2 className="h3 u-margin-bottom-base" ref={scrollToRef}>{`${
          totalResults || 0
        } ${type} die beginnen met een '${letter.toUpperCase()}'`}</h2>
        {!!items?.length && (
          <ul className="o-layout o-layout--gutter-base">
            {items.map(item => (
              <li
                key={item._id || item.key}
                className={`o-layout__cell u-fraction--6of12@from-lg ${
                  type === BROWSE_TYPE_PLACES ? 'u-fraction--4of12@from-xl' : ''
                } u-umbrella-link-container`}
              >
                <ListItem type={type} item={item} t={t} />
              </li>
            ))}
          </ul>
        )}

        <div className="o-retain--lap">
          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPrev={handlePrevPage}
              onNext={handleNextPage}
              loading={loading}
            />
          )}
        </div>
      </div>
    </Layout>
  );
}

export async function getServerSideProps({ req, res, query, locale }) {
  if (!hasAuthToken(req)) {
    res.setHeader(
      'Cache-Control',
      'public, max-age=300, s-maxage=300, stale-while-revalidate=600'
    );
  }

  if (!BROWSE_TYPES.includes(query.type)) {
    return {
      notFound: true,
    };
  }

  const { type, letter, page = 1 } = query;
  const limit = type === BROWSE_TYPE_PLACES ? 10000 : 20;

  const { items, totalResults } = await fetch(
    `${process.env.NEXT_API_ROUTE}${getFetchItemsPath({
      type,
      letter,
      page,
    })}`
  )
    .then(resp => resp.json())
    .then(data => {
      if (type === BROWSE_TYPE_PLACES) {
        return {
          items: data.aggregations.places.buckets,
          totalResults: data.aggregations.places_count.value,
        };
      }
      return {
        items: data.results,
        totalResults: data.totalResults,
      };
    });

  return {
    props: {
      href: req.url,
      query,
      limit,
      items,
      totalResults,
      ...(await serverSideTranslations(locale, ['common'])),
    },
  };
}

export default BrowseTypeAndLetterPage;
